import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChange } from '@angular/core';
import { Subscription } from 'rxjs';
import { AbstractControl } from '@angular/forms';

/**
 * Adds an error CSS class to non-Material form elements when the bound control is invalid.
 *
 * NOT needed on mat-form-field inputs — Material handles error styling natively.
 * Use for custom/non-Material inputs (lf-currency, radio groups, etc.) that need
 * visual error feedback. Pair with lf-control-messages for error text display.
 */
@Directive({
    selector: '[errorClassWhenInvalid]',
    exportAs: 'errorClassWhenInvalid',
    standalone: true,
})
export class ErrorClassWhenInvalidDirective implements OnChanges, OnDestroy
{
    // The control that will recieve the error class
    @Input()
    public errorClassWhenInvalid: AbstractControl;

    public className: string = 'error';
    public _elementRef: ElementRef;

    private subscriptions: Subscription[] = null;

    constructor(_elementRef: ElementRef) {
        this._elementRef = _elementRef;
    }

    @Input() set errorClass(className: string) {
        this.className = className;
    }

    ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {

        // This change event fires for the first time when the control is passed into the directive.
        if (changes.hasOwnProperty('control')) {

            const c = changes['control'].currentValue;

            // Initialize change listener on form.
            if (this.subscriptions === null) {
                this.subscriptions = [];
                // Initialize subscriber.
                this.subscriptions.push(
                        c.valueChanges.subscribe(() => this.changeWatcher()),
                );
                this.subscriptions.push(
                        c.statusChanges.subscribe(() => this.changeWatcher()),
                );
                this.changeWatcher();
            }
        }
    }

    ngOnDestroy() {
        for (const s in this.subscriptions) {
            if (this.subscriptions.hasOwnProperty(s) && typeof this.subscriptions[s].unsubscribe !== 'undefined') {
                this.subscriptions[s].unsubscribe();
            }
        }
        this.subscriptions = null;
    }

    public changeWatcher() {
        let foundError = false;
        if (this.errorClassWhenInvalid && this.errorClassWhenInvalid.errors) {
            for (const p in this.errorClassWhenInvalid.errors) {
                if (this.errorClassWhenInvalid.errors.hasOwnProperty(p) && (this.errorClassWhenInvalid.touched || !this.errorClassWhenInvalid.pristine)) {
                    foundError = true;
                    break;
                }
            }
        }

        if (foundError) {
            this.addErrorClass();
        } else {
            this.removeErrorClass();
        }
    }

    public addErrorClass() {
        if (this._elementRef.nativeElement.classList) {
            this._elementRef.nativeElement.classList.add(this.className);
            return;
        }

        this._elementRef.nativeElement.className += this._elementRef.nativeElement.className.length > 0
                ? ' ' + this.className
                : this.className;
    }

    public removeErrorClass() {
        if (this._elementRef.nativeElement.classList) {
            this._elementRef.nativeElement.classList.remove(this.className);
            return;
        }

        this._elementRef.nativeElement.className = this._elementRef.nativeElement.className.replace(
                new RegExp('(^|\\b)' + this.className + '(\\b|$)', 'gi'),
                ' ',
        );
    }
}
